import React from 'react';

import { OnboardingQuestionConfig } from '@Models';

interface PreferenceFieldProps {
  step: OnboardingQuestionConfig;
  value: string | string[] | undefined;
  onChange: (key: string, value: string | string[]) => void;
}

const PreferenceField: React.FC<PreferenceFieldProps> = ({ step, value, onChange }) => {
  const fieldId = `pref-${step.key}`;

  const renderInput = () => {
    if (step.type === 'input') {
      return (
        <input
          id={fieldId}
          type="text"
          value={(value as string) || ''}
          onChange={(e) => onChange(step.key, e.target.value)}
          placeholder={step.placeholder}
          maxLength={step.maxLength}
          className="w-full box-border bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/30 outline-none focus:border-[#7c6cf6] transition-colors"
        />
      );
    }

    if (step.type === 'textarea') {
      return (
        <textarea
          id={fieldId}
          value={(value as string) || ''}
          onChange={(e) => onChange(step.key, e.target.value)}
          placeholder={step.placeholder}
          maxLength={step.maxLength}
          rows={3}
          className="w-full box-border bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/30 outline-none focus:border-[#7c6cf6] transition-colors resize-none font-[inherit]"
        />
      );
    }

    // select / multi-select → pills
    const selected = step.type === 'select' ? (value ? [value as string] : []) : (value as string[]) || [];

    const handlePillClick = (option: string) => {
      if (step.type === 'select') {
        onChange(step.key, option === value ? '' : option);
      } else if (selected.includes(option)) {
        onChange(
          step.key,
          selected.filter((o) => o !== option),
        );
      } else if (!step.maxSelections || selected.length < step.maxSelections) {
        onChange(step.key, [...selected, option]);
      }
    };

    return (
      <div className="flex flex-wrap gap-2">
        {step.options?.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => handlePillClick(option)}
            className={`px-3 py-1.5 rounded-full border text-xs transition-colors duration-200 cursor-pointer focus:outline-none ${
              selected.includes(option)
                ? 'bg-[#7c6cf6]/20 border-[#7c6cf6] text-[#7c6cf6]'
                : 'bg-white/5 border-white/10 text-white/70 hover:border-white/30 hover:text-white'
            }`}>
            {option}
          </button>
        ))}
      </div>
    );
  };

  const isFormControl = step.type === 'input' || step.type === 'textarea';

  return (
    <div className="mb-5">
      {isFormControl ? (
        <label htmlFor={fieldId} className="block text-white/40 text-xs uppercase tracking-wider font-medium mb-2">
          {step.question}
        </label>
      ) : (
        <span className="block text-white/40 text-xs uppercase tracking-wider font-medium mb-2">{step.question}</span>
      )}
      {renderInput()}
    </div>
  );
};

export default PreferenceField;
